<template>
  <div class="other-costs-container">
    <div class="input-section" :class="{ 'edit-mode': editingItemId }">
      <div class="input-row">
        <div class="input-item">
          <span class="input-title">费用类型</span>
          <select v-model="newItem.type" class="input-select">
            <option value="无形资产">无形资产</option>
            <option value="其他资产">其他资产</option>
          </select>
        </div>
        <div class="input-item">
          <span class="input-title">费用名称</span>
          <input
            type="text"
            v-model="newItem.name"
            class="input-field"
            placeholder="请输入"
          />
        </div>
        <div class="input-item">
          <span class="input-title">计算依据</span>
          <input
            type="text"
            v-model="newItem.basis"
            class="input-field input-field-wide"
            placeholder="请输入"
          />
        </div>
        <div class="input-item">
          <span class="input-title">金额</span>
          <input
            type="number"
            v-model.number="newItem.amount"
            class="input-field"
            placeholder="请输入"
            step="0.01"
          />万元
        </div>
        <template v-if="editingItemId">
          <button class="btn btn-primary" @click="handleAddOrUpdate">
            修改
          </button>
          <button class="btn btn-default" @click="cancelEdit">取消</button>
        </template>
        <template v-else>
          <button class="btn btn-primary" @click="handleAddOrUpdate">
            添加
          </button>
        </template>
      </div>
    </div>
    <div class="table-container">
      <div class="table-header">
        <div class="th-cell th-seq">序号</div>
        <div class="th-cell th-name">费用名称</div>
        <div class="th-cell">测算依据</div>
        <div class="th-cell">金额（万元）</div>
        <div class="th-cell">操作</div>
      </div>
      <div class="table-body-wrapper">
        <div class="table-body">
          <template v-for="(items, category) in groupedData">
            <div class="category-row" :key="'cat-' + category">
              <div class="td-cell th-seq">{{ categoryIndex(category) }}</div>
              <div class="td-cell category-name">
                {{ categoryName(category) }}
              </div>
              <div class="td-cell"></div>
              <div class="td-cell">
                {{ formatNumber(categoryTotal(category)) }}
              </div>
              <div class="td-cell"></div>
            </div>
            <div v-for="(item, index) in items" :key="item.id" class="data-row">
              <div class="td-cell th-seq">{{ index + 1 }}</div>
              <div class="td-cell td-name">{{ item.name }}</div>
              <div class="td-cell">{{ item.basis || "-" }}</div>
              <div class="td-cell">{{ formatNumber(item.amount) }}</div>
              <div class="td-cell">
                <button class="btn-link" @click="editItem(category, item)">
                  修改
                </button>
                <button
                  class="btn-link btn-delete"
                  @click="deleteItem(category, index)"
                >
                  删除
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="table-footer">
        <div class="td-cell th-seq"></div>
        <div class="td-cell td-name">合计</div>
        <div class="td-cell"></div>
        <div class="td-cell">{{ formatNumber(totalAmount) }}</div>
        <div class="td-cell"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { totalTable, amortization } from "@/utils/comp.js";

export default {
  name: "OtherConstructionCosts",
  data() {
    return {
      newItem: {
        type: "其他资产",
        name: "",
        amount: 0,
        basis: "",
      },
      otherConstructionCostsData: {
        无形资产: [],
        其他资产: [],
      },
      editingItemId: null,
      editingOldType: null,
      projectData: {},
    };
  },
  computed: {
    groupedData() {
      return this.otherConstructionCostsData;
    },
    totalAmount() {
      let sum = 0;
      for (const [category, items] of Object.entries(
        this.otherConstructionCostsData
      )) {
        const categorySum = items.reduce((total, item) => {
          const itemAmount = item.amount || 0;
          return total + itemAmount;
        }, 0);
        sum += categorySum;
      }
      return sum;
    },
  },
  watch: {
    otherConstructionCostsData: {
      handler() {
        this.updateVuex();
      },
      deep: true,
    },
  },
  mounted() {
    this.initData();
    this.loadFromVuex();
  },

  methods: {
    // 初始化默认数据结构
    initData() {
      if (!this.otherConstructionCostsData) {
        this.otherConstructionCostsData = {
          无形资产: [],
          其他资产: [],
        };
      }
    },
    categoryIndex(category) {
      const indexMap = {
        无形资产: "一",
        其他资产: "二",
      };
      return indexMap[category] || category.charAt(0);
    },
    categoryName(category) {
      return category;
    },
    categoryTotal(category) {
      const items = this.otherConstructionCostsData[category] || [];
      return items.reduce((sum, item) => sum + (item.amount || 0), 0);
    },
    loadFromVuex() {
      this.projectData = this.$store.state.projectData || {};
      const investmentEstimate = this.projectData["总投资估算"] || {};
      const otherCostsData = investmentEstimate["工程建设其他费用"] || {};

      // 如果没有数据或数据格式不正确，使用默认结构
      if (!otherCostsData || !this.isValidData(otherCostsData)) {
        // 使用默认空数据
        this.otherConstructionCostsData = {
          无形资产: [],
          其他资产: [],
        };
      } else {
        // 完整加载Vuex中的数据
        this.setFormData(otherCostsData);
      }

      // 加载完成后立即同步到Vuex，确保数据完整性
      this.$nextTick(() => {
        this.updateVuex();
      });
    },

    // 验证数据格式是否正确
    isValidData(data) {
      if (!data) return false;

      // 检查是否有正确的数据结构 (data.data形式)
      if (
        data.data &&
        (Array.isArray(data.data.无形资产) || Array.isArray(data.data.其他资产))
      ) {
        return true;
      }

      // 检查是否有直接的数据数组
      if (Array.isArray(data["无形资产"]) || Array.isArray(data["其他资产"])) {
        return true;
      }

      // 检查是否有合计字段
      if (data.合计 !== undefined || data.totalAmount !== undefined) {
        return true;
      }

      return false;
    },

    handleAddOrUpdate() {
      if (!this.newItem.name || this.newItem.name.trim() === "") {
        this.$message.warning("请输入费用名称");
        return;
      }

      if (this.editingItemId) {
        this.updateItem();
      } else {
        this.addItem();
      }
    },
    addItem() {
      const newItem = {
        id: Date.now(),
        name: this.newItem.name.trim(),
        amount: this.newItem.amount || 0,
        basis: this.newItem.basis.trim(),
        type: this.newItem.type,
      };

      // 使用深拷贝确保Vue响应式系统能检测到变化
      const newData = {
        ...this.otherConstructionCostsData,
        [this.newItem.type]: [
          ...(this.otherConstructionCostsData[this.newItem.type] || []),
          newItem,
        ],
      };

      this.otherConstructionCostsData = newData;
      this.resetForm();

      // 强制触发视图更新
      this.$forceUpdate();

      this.updateVuex(); // 添加后立即保存到Vuex

      // 触发计算
      this.$nextTick(() => {
        totalTable();
        amortization();
      });
    },
    updateItem() {
      const oldItems =
        this.otherConstructionCostsData[this.editingOldType] || [];
      const itemIndex = oldItems.findIndex(
        (item) => item.id === this.editingItemId
      );

      if (itemIndex !== -1) {
        const updatedItem = {
          ...oldItems[itemIndex],
          name: this.newItem.name.trim(),
          amount: this.newItem.amount || 0,
          basis: this.newItem.basis.trim(),
          type: this.newItem.type,
        };

        const newData = {
          ...this.otherConstructionCostsData,
        };

        if (this.editingOldType === this.newItem.type) {
          // 同类型修改：创建新的数组来替换
          const newItems = [...newData[this.editingOldType]];
          newItems[itemIndex] = updatedItem;
          newData[this.editingOldType] = newItems;
        } else {
          // 跨类型移动：先删除原项目，再添加到新类型
          const oldTypeItems = [...newData[this.editingOldType]];
          oldTypeItems.splice(itemIndex, 1);
          newData[this.editingOldType] = oldTypeItems;
          newData[this.newItem.type] = [
            ...(newData[this.newItem.type] || []),
            updatedItem,
          ];
        }

        this.otherConstructionCostsData = newData;

        // 强制触发视图更新
        this.$forceUpdate();
      }

      this.resetForm();
      this.updateVuex(); // 修改后立即保存到Vuex

      // 触发计算
      this.$nextTick(() => {
        totalTable();
        amortization();
      });
    },
    editItem(category, item) {
      this.newItem = {
        type: item.type || category,
        name: item.name,
        amount: item.amount,
        basis: item.basis || "",
      };
      this.editingItemId = item.id;
      this.editingOldType = item.type || category;
    },
    deleteItem(category, index) {
      // 使用深拷贝确保Vue响应式系统能检测到变化
      const newData = {
        ...this.otherConstructionCostsData,
      };
      const currentItems = [
        ...(this.otherConstructionCostsData[category] || []),
      ];
      currentItems.splice(index, 1);
      newData[category] = currentItems;

      this.otherConstructionCostsData = newData;

      // 强制触发视图更新
      this.$forceUpdate();

      this.updateVuex(); // 删除后立即保存到Vuex

      // 触发计算
      this.$nextTick(() => {
        totalTable();
        amortization();
      });
    },
    cancelEdit() {
      this.resetForm();
    },
    resetForm() {
      this.newItem = {
        type: "其他资产",
        name: "",
        amount: 0,
        basis: "",
      };
      this.editingItemId = null;
      this.editingOldType = null;
    },
    getFormData() {
      return {
        合计: this.totalAmount,
        data: {
          无形资产: this.otherConstructionCostsData["无形资产"] || [],
          其他资产: this.otherConstructionCostsData["其他资产"] || [],
        },
      };
    },
    setFormData(data) {
      if (!data) return;

      let dataToUse = null;

      // 适配新的"data"结构（英文data字段）
      if (data.data) {
        dataToUse = data.data;
      }
      // 兼容旧的otherConstructionCostsData结构
      else if (data.otherConstructionCostsData) {
        dataToUse = data.otherConstructionCostsData;
      }
      // 直接包含无形资产和其他资产数组的结构
      else if (
        Array.isArray(data["无形资产"]) ||
        Array.isArray(data["其他资产"])
      ) {
        dataToUse = data;
      }
      // 兼容totalAmount结构
      else if (data.合计 !== undefined || data.totalAmount !== undefined) {
        dataToUse = {
          无形资产: data["无形资产"] || [],
          其他资产: data["其他资产"] || [],
        };
      }

      if (dataToUse) {
        // 使用深拷贝确保Vue响应式系统正确追踪变化
        this.otherConstructionCostsData = {
          无形资产: JSON.parse(JSON.stringify(dataToUse["无形资产"] || [])),
          其他资产: JSON.parse(JSON.stringify(dataToUse["其他资产"] || [])),
        };

        // 确保所有项目都有必要的字段
        ["无形资产", "其他资产"].forEach((type) => {
          this.otherConstructionCostsData[type].forEach((item) => {
            if (!item.id) {
              item.id = Date.now() + Math.random();
            }
            item.amount = item.amount || 0;
            item.sequence = item.sequence || 1;
          });
        });
      }
    },
    updateVuex() {
      const formData = this.getFormData();

      // 确保数据结构完整
      if (!this.projectData.总投资估算) {
        this.projectData.总投资估算 = {};
      }
      this.projectData.总投资估算.工程建设其他费用 = formData;
      this.$store.commit("SET_PROJECT_DATA", this.projectData);

      // 触发计算
      this.$nextTick(() => {
        totalTable();
        amortization();
      });
    },
    formatNumber(num) {
      if (num === null || num === undefined || isNaN(num)) return "";
      if (num === 0) return "";
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
  },
};
</script>

<style scoped>
.other-costs-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  font-size: 14px;
  box-sizing: border-box;
}

.input-section {
  flex-shrink: 0;
  padding: 8px 15px;
  border-bottom: 1px solid #e4e7ed;
}

.input-section.edit-mode {
  border: 2px solid #409eff;
  background-color: #f0f7ff;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: nowrap;
}

.input-item {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}

.input-title {
  display: inline-block;
  margin-right: 5px;
  color: #303133;
  font-size: 14px;
  white-space: nowrap;
}

.input-select {
  width: 90px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  background: white;
}

.input-field {
  width: 100px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  box-sizing: border-box;
}

.input-field:focus {
  border-color: #409eff;
  outline: none;
}

.input-field-wide {
  width: 180px;
}

.btn {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  height: 28px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.btn-primary {
  background: #409eff;
  color: white;
  border: none;
}

.btn-primary:hover {
  background: #66b1ff;
}

.btn-default {
  background: white;
  color: #606266;
  border: 1px solid #dcdfe6;
  margin-left: 8px;
}

.btn-default:hover {
  color: #409eff;
  border-color: #409eff;
}

.table-container {
  position: relative;
  width: 99.5%;
  flex: 1;
  margin: auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
}

.table-header {
  display: flex;
  flex-shrink: 0;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
}

.table-body-wrapper {
  flex: 1;
  overflow: auto;
}

.table-body {
  display: flex;
  flex-direction: column;
  font-size: 12px;
}

.table-footer {
  display: flex;
  flex-shrink: 0;
  background: #ecf5ff;
  border-top: 1px solid #e4e7ed;
}

.th-cell {
  padding: 8px 10px;
  border-right: 1px solid #e4e7ed;
  text-align: center;
  font-weight: 600;
  color: #303133;
  font-size: 12px;
  flex: 1;
}

.th-cell:last-child {
  border-right: none;
}

.th-seq {
  flex: none;
  width: 60px;
}

.th-name {
  text-align: left;
}

.td-name {
  text-align: left;
}

.table-body .category-row,
.table-body .data-row {
  display: flex;
  border-bottom: 1px solid #e4e7ed;
}

.category-row {
  background: #f0f2f5;
  font-weight: 600;
}

.category-row .td-cell:nth-child(2) {
  text-align: center;
}

.td-cell {
  padding: 8px 10px;
  border-right: 1px solid #e4e7ed;
  color: #606266;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.td-cell:last-child {
  border-right: none;
}

.td-cell.th-seq {
  flex: none;
  width: 60px;
  text-align: center;
}

.table-body .data-row:hover {
  background: #f5f7fa;
}

.btn-link {
  background: none;
  border: none;
  color: #409eff;
  cursor: pointer;
  font-size: 12px;
  padding: 0 5px;
}

.btn-link:hover {
  text-decoration: underline;
}

.btn-delete {
  color: #f56c6c;
}

.btn-delete:hover {
  text-decoration: underline;
}
</style>
